"use client";

import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit, Trash2, X, Users, Award } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { createMRTColumnHelper } from "material-react-table";
import { Box, Tooltip } from "@mui/material";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import { baseUrl } from "../../../helper/urls";
import { useSelector } from "react-redux";
import AdminCustomTable from "../../../components/custom_table/AdminCustomTable";

const CreatePackage = () => {
  const { token } = useSelector((state) => state.auth);
  // State management
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [categoryModal, setCategoryModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [activeCard, setActiveCard] = useState(null); // 'standard', 'master', or null

  // Replace the single isLoading state with specific loading states
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [categoryCreateLoading, setCategoryCreateLoading] = useState(false);
  const [categoryUpdateLoading, setCategoryUpdateLoading] = useState(false);
  const [categoryDeleteLoading, setCategoryDeleteLoading] = useState(false);
  const [standardPackageLoading, setStandardPackageLoading] = useState(false);
  const [masterPackageLoading, setMasterPackageLoading] = useState(false);
  const [packageUpdateLoading, setPackageUpdateLoading] = useState(false);
  const [packageDeleteLoading, setPackageDeleteLoading] = useState(false);

  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    sub_title: "",
    description: "",
  });

  const [standardForm, setStandardForm] = useState({
    category_id: "",
    service_type: "",
    hours: "",
    hourly_rate: "",
    total_price: "",
    savings: "",
    plan_name: "",
    description: "",
    flat_fee: "",
  });

  const [masterForm, setMasterForm] = useState({
    category_id: "",
    service_type: "",
    hours: "",
    hourly_rate: "",
    total_price: "",
    savings: "",
    plan_name: "",
    description: "",
    flat_fee: "",
  });

  const apiConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  // API Functions
  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await axios.get(
        `${baseUrl}/api/dyn/package/category`,
        apiConfig
      );
      setCategories(response.data.data || []);
    } catch (error) {
      toast.error("Failed to fetch categories");
      console.error(error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      setPackagesLoading(true);
      const response = await axios.get(
        `${baseUrl}/api/dyn/package/plan`,
        apiConfig
      );
      setPackages(response.data.data || []);
    } catch (error) {
      toast.error("Failed to fetch packages");
      console.error(error);
    } finally {
      setPackagesLoading(false);
    }
  };

  const createCategory = async () => {
    if (!categoryForm.name || !categoryForm.sub_title) {
      toast.error("Name and Sub Title are required");
      return;
    }

    try {
      setCategoryCreateLoading(true);
      const res = await axios.post(
        `${baseUrl}/api/dyn/package/category`,
        categoryForm,
        apiConfig
      );
      console.log(res.data, "res data>>>>>>>>>>");

      if (res.data.success == true) {
        toast.success(res.data.message);
        setCategoryForm({ name: "", sub_title: "", description: "" });
        fetchCategories();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to create category";
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setCategoryCreateLoading(false);
    }
  };

  // const createCategory = async () => {
  //   if (!categoryForm.name || !categoryForm.sub_title) {
  //     toast.error("Name and Sub Title are required");
  //     return;
  //   }

  //   try {
  //     setCategoryCreateLoading(true);
  //     await axios.post(
  //       `${baseUrl}/api/dyn/package/category`,
  //       categoryForm,
  //       apiConfig
  //     );
  //     toast.success("Category created successfully");
  //     setCategoryForm({ name: "", sub_title: "", description: "" });
  //     fetchCategories();
  //   } catch (error) {
  //     toast.error("Failed to create category");
  //     console.error(error);
  //   } finally {
  //     setCategoryCreateLoading(false);
  //   }
  // };

  const updateCategory = async () => {
    if (!editingCategory || !categoryForm.name || !categoryForm.sub_title)
      return;

    try {
      setCategoryUpdateLoading(true);
      await axios.put(
        `${baseUrl}/api/dyn/package/category/${editingCategory.id}`,
        categoryForm,
        apiConfig
      );
      toast.success("Category updated successfully");
      setEditingCategory(null);
      setCategoryForm({ name: "", sub_title: "", description: "" });
      fetchCategories();
    } catch (error) {
      toast.error("Failed to update category");
      console.error(error);
    } finally {
      setCategoryUpdateLoading(false);
    }
  };

  const deleteCategory = async (id) => {
    const result = await Swal.fire({
      html: `<div style="font-size: 30px; font-weight: bold;">Are you sure?</div>`,
      text: "You won't be able to revert this!",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      setCategoryDeleteLoading(true);
      await axios.delete(
        `${baseUrl}/api/dyn/package/category/${id}`,
        apiConfig
      );
      toast.success("Category deleted successfully");
      Swal.fire("Deleted!", "Category has been deleted.", "success");
      fetchCategories();
    } catch (error) {
      toast.error("Failed to delete category");
      Swal.fire("Error!", "Failed to delete category.", "error");
      console.error(error);
    } finally {
      setCategoryDeleteLoading(false);
    }
  };

  const createPackage = async (packageData, type) => {
    try {
      if (type === "standard") {
        setStandardPackageLoading(true);
      } else {
        setMasterPackageLoading(true);
      }

      const payload = {
        category_id: Number(packageData.category_id),
        hours: Number(packageData.hours) || 0,
        hourly_rate: Number.parseFloat(packageData.hourly_rate) || 0,
        total_price: Number.parseFloat(packageData.total_price) || 0,
        savings: Number.parseFloat(packageData.savings) || 0,
      };

      if (type === "standard") {
        payload.plan_name = packageData.plan_name;
        payload.description = packageData.description;
        payload.flat_fee = Number.parseFloat(packageData.flat_fee) || 0;
      } else {
        payload.plan_name = packageData.plan_name;
        payload.description = packageData.description;
        payload.flat_fee = Number.parseFloat(packageData.flat_fee) || 0;
      }

      await axios.post(`${baseUrl}/api/dyn/package/plan`, payload, apiConfig);
      toast.success("Package created successfully");

      // Reset forms
      if (type === "standard") {
        setStandardForm({
          category_id: "",
          service_type: "",
          hours: "",
          hourly_rate: "",
          total_price: "",
          savings: "",
          plan_name: "",
          description: "",
          flat_fee: "",
        });
      } else {
        setMasterForm({
          category_id: "",
          service_type: "",
          hours: "",
          hourly_rate: "",
          total_price: "",
          savings: "",
          plan_name: "",
          description: "",
          flat_fee: "",
        });
      }

      fetchPackages();
    } catch (error) {
      toast.error("Failed to create package");
      console.error(error);
    } finally {
      if (type === "standard") {
        setStandardPackageLoading(false);
      } else {
        setMasterPackageLoading(false);
      }
    }
  };

  const updatePackage = async () => {
    if (!editingPackage) return;

    try {
      setPackageUpdateLoading(true);

      // Determine which form to use based on active card
      const formData = activeCard === "standard" ? standardForm : masterForm;

      const payload = {
        category_id: Number(formData.category_id),
        hours: Number(formData.hours) || 0,
        hourly_rate: Number.parseFloat(formData.hourly_rate) || 0,
        total_price: Number.parseFloat(formData.total_price) || 0,
        savings: Number.parseFloat(formData.savings) || 0,
        plan_name: formData.plan_name,
        description: formData.description,
        flat_fee: Number.parseFloat(formData.flat_fee) || 0,
      };

      await axios.put(
        `${baseUrl}/api/dyn/package/plan/${editingPackage.id}`,
        payload,
        apiConfig
      );
      toast.success("Package updated successfully");

      // Reset edit state
      setEditingPackage(null);
      setActiveCard(null);

      // Reset forms
      setStandardForm({
        category_id: "",
        service_type: "",
        hours: "",
        hourly_rate: "",
        total_price: "",
        savings: "",
        plan_name: "",
        description: "",
        flat_fee: "",
      });
      setMasterForm({
        category_id: "",
        service_type: "",
        hours: "",
        hourly_rate: "",
        total_price: "",
        savings: "",
        plan_name: "",
        description: "",
        flat_fee: "",
      });

      fetchPackages();
    } catch (error) {
      toast.error("Failed to update package");
      console.error(error);
    } finally {
      setPackageUpdateLoading(false);
    }
  };

  const deletePackage = async (id) => {
    const result = await Swal.fire({
      html: `<div style="font-size: 30px; font-weight: bold;">Are you sure?</div>`,
      text: "You won't be able to revert this package deletion!",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      setPackageDeleteLoading(true);
      await axios.delete(`${baseUrl}/api/dyn/package/plan/${id}`, apiConfig);
      toast.success("Package deleted successfully");
      Swal.fire("Deleted!", "Package has been deleted.", "success");
      fetchPackages();
    } catch (error) {
      toast.error("Failed to delete package");
      Swal.fire("Error!", "Failed to delete package.", "error");
      console.error(error);
    } finally {
      setPackageDeleteLoading(false);
    }
  };

  // Helper functions
  const startEditingCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      sub_title: category.sub_title,
      description: category.description || "",
    });
  };

  const cancelCategoryEdit = () => {
    setEditingCategory(null);
    setCategoryForm({ name: "", sub_title: "", description: "" });
  };

  const startEditingPackage = (pkg) => {
    setEditingPackage(pkg);

    // Determine which card to activate based on package category
    const packageCategory = categories.find(
      (cat) => cat.id === pkg.category_id
    );
    const cardType =
      packageCategory?.name === "Standard Counselors" ? "standard" : "master";
    setActiveCard(cardType);

    // Populate the appropriate form
    const formData = {
      category_id: pkg.category_id.toString(),
      service_type: packageCategory?.sub_title || "",
      hours: pkg.hours?.toString() || "",
      hourly_rate: pkg.hourly_rate?.toString() || "",
      total_price: pkg.total_price?.toString() || "",
      savings: pkg.savings?.toString() || "",
      plan_name: pkg.plan_name || "",
      description: pkg.description || "",
      flat_fee: pkg.flat_fee?.toString() || "",
    };

    if (cardType === "standard") {
      setStandardForm(formData);
    } else {
      setMasterForm(formData);
    }

    // Scroll to the cards section
    const cardsContainer = document.querySelector(".cards-container");
    if (cardsContainer) {
      cardsContainer.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const cancelPackageEdit = () => {
    setEditingPackage(null);
    setActiveCard(null);

    // Reset forms
    setStandardForm({
      category_id: "",
      service_type: "",
      hours: "",
      hourly_rate: "",
      total_price: "",
      savings: "",
      plan_name: "",
      description: "",
      flat_fee: "",
    });
    setMasterForm({
      category_id: "",
      service_type: "",
      hours: "",
      hourly_rate: "",
      total_price: "",
      savings: "",
      plan_name: "",
      description: "",
      flat_fee: "",
    });
  };

  // Load data on component mount
  useEffect(() => {
    fetchCategories();
    fetchPackages();
  }, []);

  const standardCategories = categories.filter(
    (cat) => cat.name === "Standard Counselors"
  );
  const masterCategories = categories.filter(
    (cat) => cat.name === "Master Consultants"
  );
  const columnHelper = createMRTColumnHelper();
  const columns = [
    columnHelper.accessor("Actions", {
      header: "Actions",
      Cell: ({ cell }) => {
        const row = cell.row.original;

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Edit" arrow>
              <button
                onClick={() => startEditingPackage(row)}
                disabled={packageUpdateLoading || packageDeleteLoading}
                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
              >
                <Icon icon="lucide:edit" />
              </button>
            </Tooltip>
            <Tooltip title="Delete" arrow>
              <button
                onClick={() => deletePackage(row.id)}
                disabled={packageDeleteLoading || packageUpdateLoading}
                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                style={{ cursor: "pointer" }}
              >
                <Icon icon="mingcute:delete-2-line" />
              </button>
            </Tooltip>
          </Box>
        );
      },
    }),
    columnHelper.accessor("name", { header: "Name" }),
    columnHelper.accessor("sub_title", { header: "Sub Title" }),
    columnHelper.accessor("plan_name", { header: "Plan" }),
    columnHelper.accessor("hours", { header: "Hours" }),
    columnHelper.accessor("hourly_rate", {
      header: "Hourly Rate",
      Cell: ({ cell }) => {
        const rate = cell.getValue();
        return rate ? `$${rate}` : "-";
      },
    }),
    columnHelper.accessor("total_price", {
      header: "Total Price",
      Cell: ({ cell }) => {
        const price = cell.getValue();
        return price ? `$${price}` : "-";
      },
    }),
    columnHelper.accessor("savings", {
      header: "Savings",
      Cell: ({ cell }) => {
        const savings = cell.getValue();
        return savings ? `$${savings}` : "-";
      },
    }),
    columnHelper.accessor("flat_fee", {
      header: "Flat Fee",
      Cell: ({ cell }) => {
        const fee = cell.getValue();
        return fee ? `$${fee}` : "-";
      },
    }),
    columnHelper.accessor("description", {
      header: "Description",
      Cell: ({ cell }) => {
        const desc = cell.getValue() || "-";
        return desc.length > 30 ? desc.slice(0, 30) + "..." : desc;
      },
    }),
  ];

  return (
    <>
      <div className="package-management-container">
        <ToastContainer />
        {/* Header */}
        <div className="header-section">
          <div className="header-content">
            <h4 className="main-title">Package Management</h4>
          </div>
          <button
            className="btn-primary"
            onClick={() => setCategoryModal(true)}
          >
            <Plus className="icon-sm" />
            Manage Categories
          </button>
        </div>

        {/* Package Creation Cards */}
        <div className="cards-container">
          {/* Standard Counselors Card */}
          <div
            className={`package-card standard-card ${activeCard === "standard"
                ? "active-card"
                : activeCard === "master"
                  ? "disabled-card"
                  : ""
              }`}
          >
            <div className="card-header">
              <div className="card-icon standard-icon">
                <Users className="icon-md" />
              </div>
              <div className="card-title-section">
                <h4 className="card-title">Standard Counselors</h4>
                {editingPackage && activeCard === "standard" && (
                  <span className="edit-indicator">Editing Mode</span>
                )}
              </div>
            </div>

            <div className="card-content">
              <div className="form-group">
                <label className="form-label">Service</label>
                <select
                  className="form-select"
                  value={standardForm.category_id || ""}
                  onChange={(e) =>
                    setStandardForm({
                      ...standardForm,
                      category_id: e.target.value,
                      service_type:
                        standardCategories.find(
                          (cat) => cat.id === Number(e.target.value)
                        )?.sub_title || "",
                    })
                  }
                  disabled={activeCard === "master"}
                >
                  <option value="">Select Service</option>
                  {standardCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.sub_title} - {`(${cat.name})`}
                    </option>
                  ))}
                </select>
              </div>

              {standardForm.service_type === "Full Support Counseling Plans" ? (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Plan Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={standardForm.plan_name}
                        onChange={(e) =>
                          setStandardForm({
                            ...standardForm,
                            plan_name: e.target.value,
                          })
                        }
                        placeholder="Enter plan name"
                        disabled={activeCard === "master"}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Flat Fee</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={standardForm.flat_fee}
                        onChange={(e) =>
                          setStandardForm({
                            ...standardForm,
                            flat_fee: e.target.value,
                          })
                        }
                        placeholder="0.00"
                        disabled={activeCard === "master"}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-textarea"
                      value={standardForm.description}
                      onChange={(e) =>
                        setStandardForm({
                          ...standardForm,
                          description: e.target.value,
                        })
                      }
                      placeholder="Enter plan description (optional)"
                      rows={3}
                      disabled={activeCard === "master"}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Hours</label>
                      <input
                        type="number"
                        className="form-input"
                        value={standardForm.hours}
                        onChange={(e) =>
                          setStandardForm({
                            ...standardForm,
                            hours: e.target.value,
                          })
                        }
                        placeholder="0"
                        disabled={activeCard === "master"}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Hourly Rate</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={standardForm.hourly_rate}
                        onChange={(e) =>
                          setStandardForm({
                            ...standardForm,
                            hourly_rate: e.target.value,
                          })
                        }
                        placeholder="0.00"
                        disabled={activeCard === "master"}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Total Price</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={standardForm.total_price}
                        onChange={(e) =>
                          setStandardForm({
                            ...standardForm,
                            total_price: e.target.value,
                          })
                        }
                        placeholder="0.00"
                        disabled={activeCard === "master"}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Savings</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={standardForm.savings}
                        onChange={(e) =>
                          setStandardForm({
                            ...standardForm,
                            savings: e.target.value,
                          })
                        }
                        placeholder="0.00"
                        disabled={activeCard === "master"}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="card-actions">
                {editingPackage && activeCard === "standard" ? (
                  <>
                    <button
                      className="btn-create standard-btn"
                      onClick={updatePackage}
                      disabled={packageUpdateLoading}
                    >
                      {packageUpdateLoading ? "Updating..." : "Update Package"}
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={cancelPackageEdit}
                      disabled={packageUpdateLoading}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="btn-create standard-btn"
                    onClick={() => createPackage(standardForm, "standard")}
                    disabled={
                      standardPackageLoading ||
                      !standardForm.service_type ||
                      !standardForm.category_id ||
                      activeCard === "master"
                    }
                  >
                    {standardPackageLoading
                      ? "Creating..."
                      : "Create Standard Package"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Master Consultants Card */}
          <div
            className={`package-card master-card ${activeCard === "master"
                ? "active-card"
                : activeCard === "standard"
                  ? "disabled-card"
                  : ""
              }`}
          >
            <div className="card-header">
              <div className="card-icon master-icon">
                <Award className="icon-md" />
              </div>
              <div className="card-title-section">
                <h4 className="card-title">Master Consultants</h4>
                {editingPackage && activeCard === "master" && (
                  <span className="edit-indicator">Editing Mode</span>
                )}
              </div>
            </div>

            <div className="card-content">
              <div className="form-group">
                <label className="form-label">Category Type *</label>
                <select
                  className="form-select"
                  value={masterForm.category_id || ""}
                  onChange={(e) =>
                    setMasterForm({
                      ...masterForm,
                      category_id: e.target.value,
                      service_type:
                        masterCategories.find(
                          (cat) => cat.id === Number(e.target.value)
                        )?.sub_title || "",
                    })
                  }
                  disabled={activeCard === "standard"}
                >
                  <option value="">Select Category type</option>
                  {masterCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.sub_title}
                    </option>
                  ))}
                </select>
              </div>

              {masterForm.service_type === "Full Support Counseling Plans" ? (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Plan Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={masterForm.plan_name}
                        onChange={(e) =>
                          setMasterForm({
                            ...masterForm,
                            plan_name: e.target.value,
                          })
                        }
                        placeholder="Enter plan name"
                        disabled={activeCard === "standard"}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Flat Fee</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={masterForm.flat_fee}
                        onChange={(e) =>
                          setMasterForm({
                            ...masterForm,
                            flat_fee: e.target.value,
                          })
                        }
                        placeholder="0.00"
                        disabled={activeCard === "standard"}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-textarea"
                      value={masterForm.description}
                      onChange={(e) =>
                        setMasterForm({
                          ...masterForm,
                          description: e.target.value,
                        })
                      }
                      placeholder="Enter plan description (optional)"
                      rows={3}
                      disabled={activeCard === "standard"}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Hours</label>
                      <input
                        type="number"
                        className="form-input"
                        value={masterForm.hours}
                        onChange={(e) =>
                          setMasterForm({
                            ...masterForm,
                            hours: e.target.value,
                          })
                        }
                        placeholder="0"
                        disabled={activeCard === "standard"}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Hourly Rate</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={masterForm.hourly_rate}
                        onChange={(e) =>
                          setMasterForm({
                            ...masterForm,
                            hourly_rate: e.target.value,
                          })
                        }
                        placeholder="0.00"
                        disabled={activeCard === "standard"}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Total Price</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={masterForm.total_price}
                        onChange={(e) =>
                          setMasterForm({
                            ...masterForm,
                            total_price: e.target.value,
                          })
                        }
                        placeholder="0.00"
                        disabled={activeCard === "standard"}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Savings</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={masterForm.savings}
                        onChange={(e) =>
                          setMasterForm({
                            ...masterForm,
                            savings: e.target.value,
                          })
                        }
                        placeholder="0.00"
                        disabled={activeCard === "standard"}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="card-actions">
                {editingPackage && activeCard === "master" ? (
                  <>
                    <button
                      className="btn-create master-btn"
                      onClick={updatePackage}
                      disabled={packageUpdateLoading}
                    >
                      {packageUpdateLoading ? "Updating..." : "Update Package"}
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={cancelPackageEdit}
                      disabled={packageUpdateLoading}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="btn-create master-btn"
                    onClick={() => createPackage(masterForm, "master")}
                    disabled={
                      masterPackageLoading ||
                      !masterForm.service_type ||
                      !masterForm.category_id ||
                      activeCard === "standard"
                    }
                  >
                    {masterPackageLoading
                      ? "Creating..."
                      : "Create Master Package"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Packages Table */}
        <div className="mt-2">
          <AdminCustomTable
            columns={columns}
            data={packages}
            loading={packagesLoading}
          />
        </div>
        {/* Category Modal */}
        {categoryModal && (
          <div className="modal-overlay">
            <div className="modal-container">
              <div className="modal-header">
                <h5 className="modal-title">Category Management</h5>
                <button
                  className="modal-close"
                  onClick={() => {
                    setCategoryModal(false);
                    cancelCategoryEdit();
                  }}
                  disabled={
                    categoryCreateLoading ||
                    categoryUpdateLoading ||
                    categoryDeleteLoading
                  }
                >
                  <X className="icon-sm" />
                </button>
              </div>

              <div className="modal-body">
                <div className="modal-grid">
                  {/* Category Form */}
                  <div className="category-form-section">
                    <h5 className="section-title">
                      {editingCategory ? "Edit Category" : "Create Category"}
                    </h5>

                    <div className="form-group">
                      <label className="form-label">Name *</label>
                      <select
                        className="form-select"
                        value={categoryForm.name}
                        onChange={(e) =>
                          setCategoryForm({
                            ...categoryForm,
                            name: e.target.value,
                          })
                        }
                      >
                        <option value="">Select category name</option>
                        <option value="Standard Counselors">
                          Standard Counselors
                        </option>
                        <option value="Master Consultants">
                          Master Consultants
                        </option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Sub Title *</label>
                      <select
                        className="form-select"
                        value={categoryForm.sub_title}
                        onChange={(e) =>
                          setCategoryForm({
                            ...categoryForm,
                            sub_title: e.target.value,
                          })
                        }
                      >
                        <option value="">Select sub title</option>
                        <option value="College Advising">
                          College Advising
                        </option>
                        <option value="Essay Counseling">
                          Essay Counseling
                        </option>
                        <option value="Comprehensive Service (Essay, College Advising)">
                          Comprehensive Service (Essay, College Advising)
                        </option>
                        <option value="Full Support Counseling Plans">
                          Full Support Counseling Plans
                        </option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-textarea"
                        value={categoryForm.description}
                        onChange={(e) =>
                          setCategoryForm({
                            ...categoryForm,
                            description: e.target.value,
                          })
                        }
                        placeholder="Enter category description (optional)"
                        rows={3}
                      />
                    </div>

                    <div className="form-actions">
                      {editingCategory && (
                        <button
                          className="btn-secondary"
                          onClick={cancelCategoryEdit}
                          disabled={categoryUpdateLoading}
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        className="btn-primary"
                        onClick={
                          editingCategory ? updateCategory : createCategory
                        }
                        disabled={
                          (editingCategory
                            ? categoryUpdateLoading
                            : categoryCreateLoading) ||
                          !categoryForm.name ||
                          !categoryForm.sub_title
                        }
                      >
                        {editingCategory
                          ? categoryUpdateLoading
                            ? "Updating..."
                            : "Update"
                          : categoryCreateLoading
                            ? "Creating..."
                            : "Create"}
                      </button>
                    </div>
                  </div>

                  {/* Categories List */}
                  <div className="categories-list-section">
                    <h5 className="section-title">Existing Categories</h5>
                    <div className="categories-list">
                      {categories.map((category) => (
                        <div
                          key={category.id}
                          className={`category-item ${editingCategory?.id === category.id
                              ? "editing-category"
                              : ""
                            }`}
                        >
                          <div className="category-info">
                            <div className="category-badge">
                              {category.name}
                            </div>
                            <p className="category-subtitle">
                              {category.sub_title}
                            </p>
                            {category.description && (
                              <p className="category-description">
                                {category.description}
                              </p>
                            )}
                          </div>
                          <div className="category-actions">
                            <button
                              className="btn-action edit-btn"
                              onClick={() => startEditingCategory(category)}
                              disabled={
                                categoryUpdateLoading || categoryDeleteLoading
                              }
                            >
                              <Edit className="icon-sm" />
                            </button>
                            <button
                              className="btn-action delete-btn"
                              onClick={() => deleteCategory(category.id)}
                              disabled={
                                categoryDeleteLoading || categoryUpdateLoading
                              }
                            >
                              <Trash2 className="icon-sm" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {categories.length === 0 && (
                        <div className="empty-state">
                          <p>
                            No categories found. Create your first category
                            above.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .package-management-container {
          padding: 1rem;
          margin: 0 auto;
          background: #f8fafc;
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            sans-serif;
        }

        .header-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          background: white;
          padding: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .header-content {
          flex: 1;
        }

        .main-title {
          font-size: 2rem;
          font-weight: 700;
          color: #1a202c;
          margin: 0 0 0.5rem 0;
        }

        .main-subtitle {
          color: #718096;
          margin: 0;
          font-size: 1rem;
        }

        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn-secondary {
          background: #e2e8f0;
          color: #4a5568;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #cbd5e0;
        }

        .cards-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .package-card {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .package-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #667eea, #764ba2);
        }

        .standard-card::before {
          background: linear-gradient(90deg, #4299e1, #667eea);
        }

        .master-card::before {
          background: linear-gradient(90deg, #ed8936, #dd6b20);
        }

        .package-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .active-card {
          box-shadow: 0 8px 30px rgba(72, 187, 120, 0.3);
          transform: translateY(-4px);
        }

        .active-card::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            135deg,
            rgba(72, 187, 120, 0.05),
            rgba(72, 187, 120, 0.02)
          );
          pointer-events: none;
        }

        .disabled-card {
          opacity: 0.5;
          pointer-events: none;
          position: relative;
        }

        .disabled-card::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.1);
          pointer-events: none;
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .card-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .standard-icon {
          background: linear-gradient(135deg, #e6f3ff, #cce7ff);
          color: #4299e1;
        }

        .master-icon {
          background: linear-gradient(135deg, #fff5e6, #ffe6cc);
          color: #ed8936;
        }

        .card-title-section {
          flex: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .card-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a202c;
          margin: 0 0 0.25rem 0;
        }

        .card-subtitle {
          color: #718096;
          margin: 0;
          font-size: 0.9rem;
        }

        .edit-indicator {
          background: #48bb78;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .card-content {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .form-label {
          font-weight: 600;
          color: #2d3748;
          font-size: 0.9rem;
        }

        .form-input,
        .form-textarea {
          padding: 0.75rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.9rem;
          transition: all 0.2s;
          background: white;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .form-input:disabled,
        .form-select:disabled,
        .form-textarea:disabled {
          background: #f7fafc;
          color: #a0aec0;
          cursor: not-allowed;
        }

        .form-textarea {
          resize: vertical;
          min-height: 80px;
        }

        .card-actions {
          display: flex;
          gap: 1rem;
          margin-top: 1rem;
        }

        .btn-create {
          flex: 1;
          padding: 1rem;
          border: none;
          border-radius: 12px;
          font-weight: 700;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .standard-btn {
          background: linear-gradient(135deg, #4299e1, #667eea);
          color: white;
        }

        .master-btn {
          background: linear-gradient(135deg, #ed8936, #dd6b20);
          color: white;
        }

        .btn-create:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        }

        .btn-create:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .table-section {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .table-header {
          margin-bottom: 1.5rem;
        }

        .table-title-section {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .table-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a202c;
          margin: 0;
        }

        .table-subtitle {
          color: #718096;
          margin: 0;
          font-size: 0.9rem;
        }

        .table-container {
          overflow-x: auto;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
        }

        .packages-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
        }

        .packages-table th {
          background: #f7fafc;
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: #2d3748;
          border-bottom: 2px solid #e2e8f0;
          font-size: 0.9rem;
        }

        .packages-table td {
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: top;
        }

        .packages-table tr:hover {
          background: #f7fafc;
        }

        .service-cell {
          min-width: 200px;
        }

        .service-name {
          font-weight: 600;
          color: #1a202c;
          margin: 0 0 0.25rem 0;
        }

        .service-desc {
          font-size: 0.8rem;
          color: #718096;
          margin: 0;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .btn-action {
          padding: 0.5rem;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .edit-btn {
          background: #e6f3ff;
          color: #4299e1;
        }

        .edit-btn:hover:not(:disabled) {
          background: #bee3f8;
        }

        .delete-btn {
          background: #fed7d7;
          color: #e53e3e;
        }

        .delete-btn:hover:not(:disabled) {
          background: #feb2b2;
        }

        .btn-action:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .empty-state {
          text-align: center;
          padding: 3rem;
          color: #718096;
          font-style: italic;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-container {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 1000px;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .modal-header {
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          padding: 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
          margin: 0;
        }

        .modal-close {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }

        .modal-close:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.1);
        }

        .modal-body {
          padding: 2rem;
          max-height: calc(90vh - 100px);
          overflow-y: auto;
        }

        .modal-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }

        .category-form-section,
        .categories-list-section {
          background: #f7fafc;
          padding: 1.5rem;
          border-radius: 12px;
        }

        .section-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #1a202c;
          margin: 0 0 1rem 0;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          margin-top: 1rem;
        }

        .form-actions .btn-primary,
        .form-actions .btn-secondary {
          flex: 1;
        }

        .categories-list {
          max-height: 400px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .category-item {
          background: white;
          padding: 1rem;
          border-radius: 8px;
          border: 2px solid transparent;
          transition: all 0.2s;
        }

        .category-item:hover {
          border-color: #e2e8f0;
        }

        .editing-category {
          border-color: #48bb78 !important;
          background: #f0fff4 !important;
        }

        .category-info {
          flex: 1;
          margin-bottom: 0.5rem;
        }

        .category-badge {
          display: inline-block;
          background: #667eea;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .category-subtitle {
          font-weight: 600;
          color: #1a202c;
          margin: 0 0 0.25rem 0;
          font-size: 0.9rem;
        }

        .category-description {
          color: #718096;
          font-size: 0.8rem;
          margin: 0;
        }

        .category-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
        }

        .icon-sm {
          width: 16px;
          height: 16px;
        }

        .icon-md {
          width: 24px;
          height: 24px;
        }

        @media (max-width: 768px) {
          .package-management-container {
            padding: 1rem;
          }

          .header-section {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }

          .cards-container {
            grid-template-columns: 1fr;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .modal-grid {
            grid-template-columns: 1fr;
          }

          .table-container {
            font-size: 0.8rem;
          }

          .packages-table th,
          .packages-table td {
            padding: 0.5rem;
          }

          .card-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </>
  );
};

export default CreatePackage;
